import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";

// user_id → organization_members.role for ONE org. No status filter on purpose:
// an inactive closer's old dials must still open the closer course (9 of 12
// closer member rows are inactive). Cached per org for the session; roles
// change rarely and a stale role only picks a reference doc, never a number.
const cache = new Map<string, Promise<Map<string, string>>>();

const fetchRoles = (orgId: string): Promise<Map<string, string>> => {
  const hit = cache.get(orgId);
  if (hit) return hit;
  const p = (async () => {
    const map = new Map<string, string>();
    if (!supabase) return map;
    const { data, error } = await supabase
      .from("organization_members")
      .select("user_id, role")
      .eq("organization_id", orgId);
    if (error) {
      logger.error("useMemberRoles: fetch failed", error);
      cache.delete(orgId); // let the next mount retry
      return map;
    }
    for (const r of (data ?? []) as Array<{ user_id: string | null; role: string | null }>) {
      if (r.user_id && r.role) map.set(r.user_id, r.role);
    }
    // An empty result (session not ready, or RLS returned 0 rows) is never
    // cached, so the next mount retries instead of pinning the setter track.
    if (map.size === 0) cache.delete(orgId);
    return map;
  })();
  cache.set(orgId, p);
  return p;
};

/** Returns roleOf(userId) → that member's role in `orgId`, or null while
 *  loading / unknown / no org. */
export function useMemberRoles(
  orgId: string | null | undefined
): (userId: string | null | undefined) => string | null {
  const [state, setState] = useState<{ orgId: string; roles: Map<string, string> } | null>(null);
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    fetchRoles(orgId).then((roles) => {
      if (!cancelled) setState({ orgId, roles });
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);
  const roles = state && state.orgId === orgId ? state.roles : null;
  return useCallback(
    (userId: string | null | undefined) => (userId && roles ? (roles.get(userId) ?? null) : null),
    [roles]
  );
}
